import React, { useState, useEffect, useRef } from 'react';
import { Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { Navbar, Nav, Container, NavDropdown, Form } from 'react-bootstrap';
import SearchBox from './SearchBox';
import { logout } from '../slices/userSlice';

const Header = () => {
  const dispatch = useDispatch();
  const headerRef = useRef(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [navExpanded, setNavExpanded] = useState(false);
  const navbarRef = useRef(null);

  const userInfo = useSelector((state) => state.user?.userInfo);
  const cartItems = useSelector((state) => state.cart?.cartItems || []);
  const cartCount = Array.isArray(cartItems)
    ? cartItems.reduce((sum, item) => sum + Number(item?.qty || 0), 0)
    : 0;
  const isGuest = !!userInfo?.isGuest;
  const displayName = isGuest ? 'GUEST' : (userInfo?.firstName || userInfo?.username || 'Account');

  const [loggingOut, setLoggingOut] = useState(false);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  // Mirrors `loggingOut` but updates synchronously (unlike setState, which
  // doesn't apply until the next render). react-bootstrap's onToggle fires
  // in the same synchronous click as this handler, so a stale closure over
  // `loggingOut` would still read `false` there -- the ref is what actually
  // blocks the dropdown's auto-close on the very click that starts it.
  const loggingOutRef = useRef(false);

  const logoutHandler = () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    setLoggingOut(true);
    dispatch(logout());
  };

  useEffect(() => {
    try {
      const light = document.getElementById('theme-light');
      const dark = document.getElementById('theme-dark');
      if (theme === 'dark') {
        if (light) light.disabled = true;
        if (dark) dark.disabled = false;
        document.documentElement.setAttribute('data-theme', 'dark');
        localStorage.setItem('theme', 'dark');
      } else {
        if (light) light.disabled = false;
        if (dark) dark.disabled = true;
        document.documentElement.setAttribute('data-theme', 'light');
        localStorage.setItem('theme', 'light');
      }
    } catch (e) {}
  }, [theme]);

  // Keep a CSS var of the header's current height for sticky offsets
  useEffect(() => {
    const updateHeaderHeight = () => {
      try {
        const h = headerRef.current?.offsetHeight || 0;
        document.documentElement.style.setProperty('--header-height', `${h}px`);
      } catch (_) {}
    };
    updateHeaderHeight();
    const onResize = () => updateHeaderHeight();
    window.addEventListener('resize', onResize);
    // Recalculate shortly after theme changes (padding/line-height can differ)
    const t = setTimeout(updateHeaderHeight, 100);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(t);
    };
  }, [theme]);

  // Close navbar on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setNavExpanded(false);
      }
    };

    if (navExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [navExpanded]);

  return (
    <header className='sticky-top' ref={headerRef}>
      <Navbar bg='primary' variant='dark' expand='lg' collapseOnSelect expanded={navExpanded} onToggle={setNavExpanded} className='shadow-sm py-2' ref={navbarRef}>
        <Container style={{ width: '100%' }}>
          <LinkContainer to='/'>
            <Navbar.Brand className='font-weight-bold d-flex align-items-center'>
              <img 
                src='/LOTUSTOR_512x512.png' 
                alt='Lotustor Logo' 
                style={{ height: '40px', width: '40px', marginRight: '8px' }}
              />
              THE LOTUSTOR
            </Navbar.Brand>
          </LinkContainer>
          
          {/* Mobile cart icon - shown only when navbar is collapsed */}
          <LinkContainer to='/cart' className='d-lg-none ml-auto'>
            <Nav.Link className='position-relative px-2'>
              <i className='fas fa-shopping-cart'></i>
              {cartCount > 0 && (
                <span
                  className='badge badge-danger badge-pill'
                  style={{ position: 'absolute', top: -4, right: -2, fontSize: '0.65rem' }}
                >
                  {cartCount}
                  <span className='sr-only'>cart items</span>
                </span>
              )}
            </Nav.Link>
          </LinkContainer>
          
          <Navbar.Toggle aria-controls='basic-navbar-nav' />
          <Navbar.Collapse id='basic-navbar-nav' className='w-100'>
            {/* Mobile: full-width search on its own row */}
            <div className='d-lg-none w-100 my-2 header-search'>
              <Route render={({ history }) => <SearchBox history={history} />} />
            </div>

            {/* Desktop: centered search */}
            <div className='d-none d-lg-flex flex-grow-1 justify-content-center px-3'>
              <div className='header-search' style={{ width: '100%', maxWidth: 560, minWidth: 360 }}>
                <Route render={({ history }) => <SearchBox history={history} />} />
              </div>
            </div>

            {/* Right aligned controls */}
            <Nav className='ml-auto align-items-center'>
              <Form.Check
                type='switch'
                id='theme-toggle'
                label={theme === 'dark' ? 'Dark' : 'Light'}
                checked={theme === 'dark'}
                onChange={(e) => setTheme(e.target.checked ? 'dark' : 'light')}
                className='mr-2'
              />

              {userInfo ? (
                <NavDropdown
                  title={displayName}
                  id='username'
                  alignRight
                  show={accountDropdownOpen}
                  onToggle={(isOpen, event, metadata) => {
                    // Ignore every toggle attempt (including the auto-close
                    // react-bootstrap fires right after the Logout item is
                    // selected) until the logout cycle actually finishes and
                    // the page redirects -- keeps Logout visibly grayed out
                    // instead of vanishing the instant it's clicked.
                    if (loggingOutRef.current) return;
                    setAccountDropdownOpen(isOpen);
                  }}
                >
                  {!isGuest && (
                    <LinkContainer to='/profile'>
                      <NavDropdown.Item>Profile</NavDropdown.Item>
                    </LinkContainer>
                  )}
                  {!isGuest && (
                    <LinkContainer to='/contactsupport'>
                      <NavDropdown.Item>Contact Support</NavDropdown.Item>
                    </LinkContainer>
                  )}
                  <NavDropdown.Item onClick={logoutHandler} disabled={loggingOut}>Logout</NavDropdown.Item>
                </NavDropdown>
              ) : (
                <LinkContainer to='/login'>
                  <Nav.Link>
                    <i className='fas fa-user'></i> Sign In
                  </Nav.Link>
                </LinkContainer>
              )}

              {userInfo && userInfo.isAdmin && (
                <NavDropdown title='Admin' id='adminmenu' alignRight>
                  <LinkContainer to='/admin/userlist'>
                    <NavDropdown.Item>Users</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/productlist'>
                    <NavDropdown.Item>Products</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/orderlist'>
                    <NavDropdown.Item>Orders</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/categories'>
                    <NavDropdown.Item>Categories</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/product/quick-import'>
                    <NavDropdown.Item>Quick Import</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/aliexpress-service-clients'>
                    <NavDropdown.Item>AliExpress Credentials</NavDropdown.Item>
                  </LinkContainer>
                </NavDropdown>
              )}

              <LinkContainer to='/cart'>
                <Nav.Link className='position-relative'>
                  <i className='fas fa-shopping-cart'></i>
                  <span className='ml-1'>Cart</span>
                  <span
                    className='badge badge-danger badge-pill'
                    style={{ position: 'absolute', top: -4, right: -6, fontSize: '0.65rem' }}
                  >
                    {cartCount}
                    <span className='sr-only'>cart items</span>
                  </span>
                </Nav.Link>
              </LinkContainer>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </header>
  );
};

export default Header;
